'use client';

import Image from 'next/image';
import Link from 'next/link';
import type React from 'react';
import { Fragment, useState, useMemo } from 'react';

import { Button } from '@/components/ui/button';
// import VocalIcon from '@/components/icons/vocalize';
import { hasVisibleText } from '@/lib/heading.utils';

// Heading Components
export const H1: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <h1
    className={`font-black text-2xl md:text-3xl lg:text-[48px] leading-[1.1] md:leading-[1.1] lg:leading-[1.1] tracking-[-2%] uppercase text-foreground break-words ${className}`}
    style={{
      lineHeight: '1.1',
    }}
  >
    {children}
  </h1>
);

export const H2: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <h2
    className={`font-black text-2xl md:text-3xl lg:text-[48px] leading-[1.1] md:leading-[1.1] lg:leading-[1.1] tracking-[-2%] uppercase text-foreground break-words ${className}`}
    style={{
      lineHeight: '1.1',
    }}
  >
    {children}
  </h2>
);

export const H3: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <h3
    className={`font-black text-xl md:text-2xl lg:text-[40px] leading-[1.1] md:leading-[1.1] lg:leading-[1.1] tracking-[-2%] uppercase text-foreground break-words ${className}`}
    style={{
      lineHeight: '1.1',
    }}
  >
    {children}
  </h3>
);

export const H4: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <h4
    className={`font-black text-lg md:text-xl lg:text-[36px] leading-[1.1] md:leading-[1.1] lg:leading-[1.1] tracking-[-2%] uppercase text-foreground break-words ${className}`}
    style={{
      lineHeight: '1.1',
    }}
  >
    {children}
  </h4>
);

export const H5: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <h5
    className={`font-black text-base md:text-lg lg:text-[32px] leading-[1.1] md:leading-[1.1] lg:leading-[1.1] tracking-[-2%] uppercase text-foreground break-words ${className}`}
    style={{
      lineHeight: '1.1',
    }}
  >
    {children}
  </h5>
);

// Body Text
export const BodyText: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <p
    className={`font-medium text-base md:text-lg leading-[140%] tracking-[-1%] text-foreground break-words ${className}`}
  >
    {children}
  </p>
);

// Quote Component
export const Quote: React.FC<{
  quote: string;
  source?: {
    href?: string;
    label?: string;
  };
  className?: string;
  align?: 'left' | 'center' | 'right';
}> = ({ quote, source, className = '', align = 'left' }) => (
  <div
    className={`bg-quote/5 p-4 md:px-4 md:py-5 rounded-lg border border-border-quote dark:border-none ${className} `}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1">
        <blockquote
          className={`font-normal text-base md:text-lg leading-[140%] tracking-[-1%] text-quote-text dark:text-white mb-1.5 break-words ${
            align === 'left' ? 'text-left' : align === 'center' ? 'text-center' : 'text-right'
          }`}
        >
          {quote}
        </blockquote>
        {!!source &&
          (source?.href ? (
            <Link href={source.href} target="_blank">
              <span className="font-medium text-sm md:text-base leading-[140%] tracking-[-1%] text-[#505CFF] hover:text-[#505CFF]/90 underline break-words">
                {source?.label}
              </span>
            </Link>
          ) : (
            !!source?.label && (
              <span className="font-medium text-sm md:text-base leading-[140%] tracking-[-1%] text-about-gray break-words">
                {source?.label}
              </span>
            )
          ))}
      </div>
      {/*<button*/}
      {/*  type="button"*/}
      {/*  aria-label="Озвучить цитату"*/}
      {/*  className="w-[30px] h-[30px] md:w-10 md:h-10 p-[7px] md:p-3 rounded-1xl bg-[#A84DFF] text-white flex items-center justify-center shrink-0"*/}
      {/*>*/}
      {/*  <span className="block md:hidden">*/}
      {/*    <VocalIcon*/}
      {/*      width={16}*/}
      {/*      height={16}*/}
      {/*    />*/}
      {/*  </span>*/}
      {/*  <span className="hidden md:block">*/}
      {/*    <VocalIcon*/}
      {/*      width={20}*/}
      {/*      height={20}*/}
      {/*    />*/}
      {/*  </span>*/}
      {/*</button>*/}
    </div>
  </div>
);

// List Item Component
export const ListItem: React.FC<{
  title: string;
  text: string;
  className?: string;
}> = ({ title, text, className = '' }) => (
  <div className={`mb-6 ${className}`}>
    <h6
      className="font-black text-lg md:text-xl lg:text-[32px] leading-[1.1] md:leading-[1.1] lg:leading-[1.1] tracking-[-2%] uppercase text-foreground mb-3 break-words"
      style={{
        lineHeight: '1.1',
      }}
    >
      {title}
    </h6>
    <p className="font-medium text-base md:text-lg leading-[140%] tracking-[-1%] text-quote-text break-words">
      {text}
    </p>
  </div>
);

// List Component for new format
export const List: React.FC<{
  title?: string;
  items: Array<{
    id: string;
    itemTitle: string;
    itemText: string;
  }>;
  bulleted?: boolean;
  style?: string;
  className?: string;
}> = ({ title, items, bulleted = false, style = 'default', className = '' }) => (
  <div className={`mb-8 ${className}`}>
    {title && (
      <h6
        className="font-black text-lg md:text-xl lg:text-[32px] leading-[1.1] md:leading-[1.1] lg:leading-[1.1] tracking-[-2%] uppercase text-foreground mb-4 break-words"
        style={{
          lineHeight: '1.1',
        }}
      >
        {title}
      </h6>
    )}
    <div className="space-y-4">
      {items.map((item) => (
        <div key={item.id} className="flex items-center space-x-3">
          {bulleted && <div className="w-2 h-2 bg-[#FF3DAE] rounded-full flex-shrink-0"></div>}
          <div className="flex-1">
            <p className="font-medium text-sm md:text-base leading-[140%] tracking-[-1%] text-foreground break-words">
              {item.itemText}
            </p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Bubble Component (Purple line + text)
export const Bubble: React.FC<{
  children: React.ReactNode;
  className?: string;
  bgColor?: string;
}> = ({ children, className = '', bgColor }) => {
  const bgClass = useMemo(() => {
    if (bgColor) {
      return bgColor;
    }
    return Math.random() < 0.5 ? 'bg-bubble dark:bg-[#25282E]' : 'bg-[#F4E9FF] dark:bg-[#A84DFF]/5';
  }, [bgColor]);

  const stripeColor = useMemo(() => {
    if (bgColor?.includes('#F4E9FF') || bgColor?.includes('#A84DFF')) {
      return 'bg-[#A84DFF]';
    }
    return 'bg-[#FFCF76] dark:bg-[#C47F00]';
  }, [bgColor]);

  return (
    <div
      className={`flex items-start space-x-4 ${bgClass} p-4 md:p-6 rounded-lg border border-border-quote dark:border-none ${className}`}
    >
      <div className={`w-1 ${stripeColor} flex-shrink-0 self-stretch min-h-full`} />
      <div className="font-medium dark:font-normal text-sm md:text-lg leading-[140%] tracking-[-1%] text-quote-text dark:text-white flex-1 break-words">
        <span className="font-bold">Примечание. </span>
        {children}
      </div>
    </div>
  );
};

// Article Image Component
export const ArticleImage: React.FC<{
  src: string;
  alt: string;
  caption?: string;
  className?: string;
}> = ({ src, alt, caption, className = '' }) => (
  <figure className={`my-6 md:my-8 ${className}`}>
    <Image
      src={src || '/placeholder.svg'}
      alt={alt}
      className="w-full rounded-2xl object-cover"
      width={1200}
      height={400}
      quality={75}
      sizes="(max-width: 768px) 100vw, 800px"
      loading="lazy"
    />
    {hasVisibleText(caption) && (
      <figcaption className="text-xs md:text-sm text-muted-foreground mt-3 font-medium break-words">
        {caption?.trim()}
      </figcaption>
    )}
  </figure>
);

// Spoiler Component
export const Spoiler: React.FC<{
  content: any;
  className?: string;
}> = ({ content, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);

  const renderInline = (child: any, index: number): React.ReactNode => {
    if (!child) return null;

    if (child.type === 'linebreak') {
      return <br key={index} />;
    }

    if (child.type === 'link' || child.type === 'autolink') {
      const href = child.url || child.href;
      const linkChildren = child.children?.map((linkChild: any, i: number) =>
        renderInline(linkChild, i),
      );
      if (!href) {
        return <Fragment key={index}>{linkChildren}</Fragment>;
      }
      return (
        <a
          key={index}
          href={href}
          target={child.target || '_blank'}
          rel={child.rel || 'noopener noreferrer'}
          className="text-blue-500 hover:text-blue-700 underline"
        >
          {linkChildren}
        </a>
      );
    }

    if (child.type === 'text' || typeof child.text === 'string') {
      let text: React.ReactNode = child.text ?? '';
      if (child.format) {
        if (child.format & 1) text = <strong>{text}</strong>;
        if (child.format & 2) text = <em>{text}</em>;
        if (child.format & 8) text = <u>{text}</u>;
      }
      return <Fragment key={index}>{text}</Fragment>;
    }

    if (Array.isArray(child.children)) {
      return (
        <Fragment key={index}>
          {child.children.map((nested: any, i: number) => renderInline(nested, i))}
        </Fragment>
      );
    }

    return null;
  };

  const renderContent = (node: any): React.ReactNode => {
    if (!node) return null;

    switch (node.type) {
      case 'root':
        return (
          <div>
            {node.children?.map((child: any, index: number) => (
              <div key={index}>{renderContent(child)}</div>
            ))}
          </div>
        );
      case 'paragraph':
        return (
          <BodyText className="text-foreground mb-4">
            {node.children?.map((child: any, index: number) => renderInline(child, index))}
          </BodyText>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`mb-6 md:mb-8 ${className}`}>
      <div className="bg-muted dark:bg-card p-4 md:p-6 rounded-lg relative border border-border">
        <div className="flex items-center justify-center mb-4 absolute top-0 left-0 w-full h-full">
          {!isOpen && (
            <Button
              onClick={() => setIsOpen(true)}
              className="bg-[#a8ea3a] hover:bg-[#a9f84a] text-[#25282e] dark:text-quote-text font-medium px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm z-10"
            >
              Открыть спойлер
            </Button>
          )}
        </div>

        <div
          className={`transition-all duration-300 ${
            isOpen ? 'opacity-100 blur-none' : 'opacity-60 blur-sm'
          }`}
        >
          {isOpen ? (
            <div className="space-y-4">{renderContent(content.root)}</div>
          ) : (
            <div className="h-16 md:h-20 bg-gradient-to-b from-transparent to-muted dark:to-card">
              {renderContent(content.root)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
